import { cn } from "@/lib/utils";
import { HTMLAttributes } from "react";

export const ContentCardSkeleton = ({
    className
}: HTMLAttributes<HTMLDivElement>) => {
    return (
        <div
            className={cn(
                "text-grey-200 motion-safe:animate-pulse dark:text-grey-50",
                className
            )}
            aria-hidden
        >
            <div className="flex w-full flex-col">
                <div
                    className="relative w-full rounded-xl bg-current
                        pt-[calc(100%/1.6)]"
                />
                <div className="mt-[4.4%] flex items-start">
                    <div
                        className="relative mr-2 flex h-8 w-8 shrink-0
                            items-center justify-center overflow-hidden
                            rounded-full bg-current"
                    />
                    <div className="flex flex-1 flex-col">
                        <div className="mb-1.5 h-6 rounded-sm bg-current" />
                        <div className="h-6 w-[70%] rounded-sm bg-current" />
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Column layouts used by the content grids:
 * - "wide": 1 column on mobile, then 3 / 4 (1.5xl) / 5 (2.5xl)
 * - "default": 1 column on mobile, then 3 / 4 (2.5xl)
 * - "auto": auto-fill columns of minmax(280px, 1fr), column count unknown to CSS
 */
type SkeletonColumns = "wide" | "default" | "auto";

// Per-card visibility so the skeleton always fills two full rows:
// 3 cards on mobile, 6 at 3 columns, 8 at 4 columns, 10 at 5 columns.
const visibility: Record<SkeletonColumns, (index: number) => string> = {
    wide: (i) =>
        cn(
            i >= 3 && i < 6 && "hidden md:block",
            i >= 6 && i < 8 && "hidden 1.5xl:block",
            i >= 8 && "hidden 2.5xl:block"
        ),
    default: (i) =>
        cn(
            i >= 3 && i < 6 && "hidden md:block",
            i >= 6 && i < 8 && "hidden 2.5xl:block",
            i >= 8 && "hidden"
        ),
    auto: (i) => cn(i >= 3 && "hidden md:block")
};

/**
 * Placeholder cards rendered directly inside a content grid, for the first load
 * and while the next page loads during infinite scroll.
 */
export const ContentGridSkeleton = ({
    columns = "default"
}: {
    columns?: SkeletonColumns;
}) => {
    const count = columns === "auto" ? 8 : 10;

    return (
        <>
            {Array.from({ length: count }, (_, i) => (
                <ContentCardSkeleton
                    key={i}
                    className={visibility[columns](i)}
                />
            ))}
        </>
    );
};
